import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { broadcastAccountData } from 'src/composables/app/system/useBroadcastChannel'
import { log } from 'src/services/app/log.service'
import { localStorageService } from 'src/services/app/storage.service'
import { notifyMsgs, notifyService } from 'src/services/app/notify.service'
import { ME_CACHE_TTL_MS, authService } from 'src/services/api/auth.service'
import { STORAGE_KEYS } from 'src/services/consts/api.const'
import { MUTATION_KEYS, QUERY_KEYS } from 'src/services/consts/queries.const'
import { wrapMutationAsync } from 'src/services/helpers/util.helper'

/**
 * Rename-account mutation for admin account settings.
 *
 * Persists via `authService.renameAccount` (POST /account/rename), partial-merges
 * into the ACCOUNT_DATA query cache, syncs ME_ACCOUNT_CACHE, then broadcasts for
 * cross-tab sync.
 *
 * Consumed via `useAccountActionsStore` — components never call this
 * composable directly (project mutation convention).
 */
export function useRenameAccountMutation() {
  const queryClient = useQueryClient()

  const renameAccountMutation = useMutation({
    mutationKey: [MUTATION_KEYS.RENAME_ACCOUNT],
    mutationFn: ({ accountName }) => authService.renameAccount({ accountName }),

    onSuccess: (partial) => {
      const merged = queryClient.setQueryData([QUERY_KEYS.ACCOUNT_DATA], (prev) => {
        if (!prev) return prev
        return { ...prev, ...partial }
      })

      if (!merged) return

      localStorageService.cacheData(STORAGE_KEYS.ME_ACCOUNT_CACHE, merged, ME_CACHE_TTL_MS)
      broadcastAccountData(merged)
      notifyService.success(notifyMsgs.success.renameAccount)
    },

    onError: (error) => {
      log.error('[Account] renameAccount failed', error)

      const status = error?.response?.status

      if (status === 409) {
        notifyService.error(notifyMsgs.error.renameAccountNameTaken)
        return
      }

      notifyService.error(notifyMsgs.error.renameAccount)
    },
  })

  return {
    renameAccount: wrapMutationAsync(renameAccountMutation.mutateAsync),
    isRenamingAccount: renameAccountMutation.isPending,
    renameAccountError: renameAccountMutation.error,
  }
}
